import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api/client";
import { X, UserCog, Archive, Ban, Unlock, Mail } from "lucide-react";

export default function BulkActionsBar({ selectedIds, users, lostReasons, onClose, onDone, onEmail }) {
  const [mode, setMode] = useState(null); // 'owner' | 'archive' | 'block' | 'unblock' | null
  const [ownerId, setOwnerId] = useState(users[0]?.id || "");
  const [reason, setReason] = useState(lostReasons?.[0] || "");
  const [note, setNote] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (label, fn) => {
    setBusy(true);
    try {
      const { data } = await fn();
      toast.success(`${label}: ${data.updated} card${data.updated === 1 ? "" : "s"}`);
      onDone?.();
      setMode(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const ids = selectedIds;
  const doOwner = () => run("Reassigned", () => api.post("/cards/bulk-owner", { card_ids: ids, owner_id: ownerId }));
  const doArchive = () => run("Archived", () => api.post("/cards/bulk-archive", { card_ids: ids, reason, note }));
  const doBlock = () => run("Blocked", () => api.post("/cards/bulk-block", { card_ids: ids, reason: blockReason }));
  const doUnblock = () => run("Unblocked", () => api.post("/cards/bulk-unblock", { card_ids: ids }));

  return (
    <div data-testid="bulk-actions-bar" className="sticky top-16 z-30 mx-4 sm:mx-6 lg:mx-8 mt-3 bg-[#15233B] text-white rounded-md shadow-lg flex flex-wrap items-center gap-2 px-4 py-2">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/80">
        {selectedIds.length} selected
      </div>
      <button onClick={onEmail} data-testid="bulk-email-btn" className="text-xs font-bold px-2.5 py-1.5 rounded bg-[#FFCB05] text-[#15233B] hover:bg-[#FBC02D] flex items-center gap-1">
        <Mail className="h-3.5 w-3.5" /> Email
      </button>
      <button onClick={() => setMode(mode === "owner" ? null : "owner")} data-testid="bulk-owner-btn" className="text-xs font-semibold px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 flex items-center gap-1">
        <UserCog className="h-3.5 w-3.5" /> Reassign
      </button>
      <button onClick={() => setMode(mode === "archive" ? null : "archive")} data-testid="bulk-archive-btn" className="text-xs font-semibold px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 flex items-center gap-1">
        <Archive className="h-3.5 w-3.5" /> Archive as Lost
      </button>
      <button onClick={() => setMode(mode === "block" ? null : "block")} data-testid="bulk-block-btn" className="text-xs font-semibold px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 flex items-center gap-1">
        <Ban className="h-3.5 w-3.5" /> Block
      </button>
      <button onClick={doUnblock} disabled={busy} data-testid="bulk-unblock-btn" className="text-xs font-semibold px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 flex items-center gap-1">
        <Unlock className="h-3.5 w-3.5" /> Unblock
      </button>
      <button onClick={onClose} data-testid="bulk-close-btn" className="ml-auto p-1.5 rounded hover:bg-white/10" title="Clear selection">
        <X className="h-4 w-4" />
      </button>

      {mode === "owner" && (
        <div className="w-full flex flex-wrap items-center gap-2 pt-2 mt-1 border-t border-white/10" data-testid="bulk-owner-form">
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className="text-xs bg-white text-[#15233B] rounded px-2 py-1.5 font-semibold">
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <button onClick={doOwner} disabled={busy} className="text-xs font-bold px-3 py-1.5 rounded bg-[#FBBF24] hover:bg-[#F59E0B]">Apply</button>
        </div>
      )}
      {mode === "archive" && (
        <div className="w-full flex flex-wrap items-center gap-2 pt-2 mt-1 border-t border-white/10" data-testid="bulk-archive-form">
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="text-xs bg-white text-[#15233B] rounded px-2 py-1.5 font-semibold">
            {(lostReasons || []).map((r) => <option key={r}>{r}</option>)}
          </select>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note" className="text-xs bg-white text-[#15233B] rounded px-2 py-1.5 flex-1 min-w-[180px]" />
          <button onClick={doArchive} disabled={busy} className="text-xs font-bold px-3 py-1.5 rounded bg-[#b91c1c] hover:bg-[#9b1818]">Archive</button>
        </div>
      )}
      {mode === "block" && (
        <div className="w-full flex flex-wrap items-center gap-2 pt-2 mt-1 border-t border-white/10" data-testid="bulk-block-form">
          <input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Reason" className="text-xs bg-white text-[#15233B] rounded px-2 py-1.5 flex-1 min-w-[180px]" />
          <button onClick={doBlock} disabled={busy} className="text-xs font-bold px-3 py-1.5 rounded bg-[#7e22ce] hover:bg-[#6b1ba8]">Block</button>
        </div>
      )}
    </div>
  );
}
